import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Eye, Calendar, Users, ArrowUp, ArrowDown, ArrowUpDown, Search, X, Trash2 } from "lucide-react";
import { useProjects } from "@/hooks/useProjects";
import { useCurrentUser, useUsers } from "@/hooks/useUsers";
import { useTherapyAreas } from "@/hooks/useMasterData";
import { projectsApi } from "@/services/projectsApi";
import { ApiError } from "@/services/api";
import { useQueryClient } from "@tanstack/react-query";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog";
import { useProjectPermissions } from "@/hooks/useProjectPermissions";
import PageLayout from "@/components/PageLayout";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { projectTypeLabel, projectTypeVariant } from "@/config/projectTypeConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

const PAGE_SIZE = 10;

type SortField = "name" | "status";
type SortDir = "asc" | "desc";

const ALL_VALUE = "__all__";

const AllProjects = () => {
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const { data: allUsers = [] } = useUsers();
  const { data: therapyAreasList = [] } = useTherapyAreas();
  const queryClient = useQueryClient();
  const { isAdmin, canCreateProjects, canEditProject } = useProjectPermissions();
  const isLoading = projectsLoading || userLoading;

  const [deletingProject, setDeletingProject] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  const [deleteBlockMessage, setDeleteBlockMessage] = useState<string | undefined>(undefined);

  const [page, setPage] = useState(1);

  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // Filter state
  const [searchText, setSearchText] = useState("");
  const [filterClient, setFilterClient] = useState<string>(ALL_VALUE);
  const [filterProjectType, setFilterProjectType] = useState<string>(ALL_VALUE);
  const [filterTherapyArea, setFilterTherapyArea] = useState<string>(ALL_VALUE);
  const [filterMember, setFilterMember] = useState<string>(ALL_VALUE);

  // The set of projects the current user is allowed to see (admin sees all, others see only their projects)
  const visibleProjects = useMemo(() => {
    return projects.filter(
      (p) => isAdmin || !currentUser || p.memberIds.includes(currentUser.id)
    );
  }, [projects, isAdmin, currentUser]);

  // Derive unique filter options from VISIBLE projects only
  const clientOptions = useMemo(() => {
    const names = new Set<string>();
    visibleProjects.forEach((p) => p.clientNames.forEach((name) => names.add(name)));
    return Array.from(names).sort();
  }, [visibleProjects]);

  const projectTypeOptions = useMemo(() => {
    const types = new Set<string>();
    visibleProjects.forEach((p) => { if (p.projectType) types.add(p.projectType); });
    return Array.from(types).sort();
  }, [visibleProjects]);

  const therapyAreaOptions = useMemo(() => {
    // Prefer master data list; fall back to project-derived values
    if (therapyAreasList.length > 0) {
      return therapyAreasList.filter((t) => t.is_active).map((t) => t.name).sort();
    }
    const areas = new Set<string>();
    visibleProjects.forEach((p) => { if (p.therapyArea) areas.add(p.therapyArea); });
    return Array.from(areas).sort();
  }, [visibleProjects, therapyAreasList]);

  const memberOptions = useMemo(() => {
    // Derive members from visible projects only
    const memberIdSet = new Set<string>();
    visibleProjects.forEach((p) => p.memberIds.forEach((id: string) => memberIdSet.add(id)));
    if (allUsers.length > 0) {
      return allUsers
        .filter((u) => u.is_active && memberIdSet.has(u.id))
        .map((u) => ({ id: u.id, label: `${u.first_name} ${u.last_name}` }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    return [];
  }, [allUsers, visibleProjects]);

  // Toggle sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 ml-1 inline opacity-50" />;
    return sortDir === "asc"
      ? <ArrowUp className="w-3.5 h-3.5 ml-1 inline" />
      : <ArrowDown className="w-3.5 h-3.5 ml-1 inline" />;
  };

  // Clear all filters helper
  const hasActiveFilters = searchText || filterClient !== ALL_VALUE || filterProjectType !== ALL_VALUE || filterTherapyArea !== ALL_VALUE || filterMember !== ALL_VALUE;
  const clearFilters = () => {
    setSearchText("");
    setFilterClient(ALL_VALUE);
    setFilterProjectType(ALL_VALUE);
    setFilterTherapyArea(ALL_VALUE);
    setFilterMember(ALL_VALUE);
    setPage(1);
  };

  const handleDeleteClick = (project: { id: string; name: string }) => {
    setDeleteBlocked(false);
    setDeleteBlockMessage(undefined);
    setDeletingProject(project);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingProject) return;
    setIsDeleting(true);
    setDeleteBlocked(false);
    setDeleteBlockMessage(undefined);
    try {
      await projectsApi.delete(deletingProject.id);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setDeletingProject(null);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        setDeleteBlocked(true);
        setDeleteBlockMessage("Cannot delete this project because it has active jobs running. Please wait for them to complete or cancel them first.");
      } else {
        setDeleteBlocked(true);
        setDeleteBlockMessage("Failed to delete the project. Please try again.");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const filtered = useMemo(() => {
    let result = [...visibleProjects];

    // Apply text search
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.code.toLowerCase().includes(q) ||
          p.clientNames.some((name) => name.toLowerCase().includes(q))
      );
    }

    // Apply structured filters
    if (filterClient !== ALL_VALUE) {
      result = result.filter((p) => p.clientNames.includes(filterClient));
    }
    if (filterProjectType !== ALL_VALUE) {
      result = result.filter((p) => p.projectType === filterProjectType);
    }
    if (filterTherapyArea !== ALL_VALUE) {
      result = result.filter((p) => p.therapyArea === filterTherapyArea);
    }
    if (filterMember !== ALL_VALUE) {
      result = result.filter((p) => p.memberIds.includes(filterMember));
    }

    // Apply sorting
    if (sortField) {
      result.sort((a, b) => {
        let cmp = 0;
        if (sortField === "name") {
          cmp = a.name.localeCompare(b.name);
        } else if (sortField === "status") {
          cmp = a.status.localeCompare(b.status);
        }
        return sortDir === "asc" ? cmp : -cmp;
      });
    } else {
      // Default sort by createdAt desc
      result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }

    return result;
  }, [visibleProjects, searchText, filterClient, filterProjectType, filterTherapyArea, filterMember, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("ellipsis");
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("ellipsis");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <PageLayout>
      <PageHeader title="Projects" subtitle="View and manage all projects" />

      {canCreateProjects && (
        <div className="flex justify-end mb-4">
          <Button asChild size="sm" className="gap-1 bg-brand-teal text-white hover:bg-brand-teal/90">
            <Link to="/projects/new">
              <Plus className="w-4 h-4" /> Start New Project
            </Link>
          </Button>
        </div>
      )}

      {/* Filter Block */}
      <div className="rounded-lg border bg-card p-4 mb-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          {/* Free-text search */}
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code or client…"
                value={searchText}
                onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
                className="pl-9"
              />
            </div>
          </div>

          {/* Client filter */}
          <div className="w-[180px]">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Client</label>
            <Select value={filterClient} onValueChange={(v) => { setFilterClient(v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>All Clients</SelectItem>
                {clientOptions.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Project Type filter */}
          <div className="w-[180px]">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Project Type</label>
            <Select value={filterProjectType} onValueChange={(v) => { setFilterProjectType(v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>All Types</SelectItem>
                {projectTypeOptions.map((t) => (
                  <SelectItem key={t} value={t}>{projectTypeLabel[t] ?? t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Therapy Area filter */}
          <div className="w-[200px]">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Therapy Area</label>
            <Select value={filterTherapyArea} onValueChange={(v) => { setFilterTherapyArea(v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Therapy Areas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>All Therapy Areas</SelectItem>
                {therapyAreaOptions.map((ta) => (
                  <SelectItem key={ta} value={ta}>{ta}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Team Members filter */}
          <div className="w-[200px]">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Team Member</label>
            <Select value={filterMember} onValueChange={(v) => { setFilterMember(v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Members" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>All Members</SelectItem>
                {memberOptions.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Clear filters button */}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-muted-foreground">
              <X className="w-3.5 h-3.5" /> Clear
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <button
                  type="button"
                  className="inline-flex items-center font-medium hover:text-foreground transition-colors"
                  onClick={() => handleSort("name")}
                >
                  Name {sortIcon("name")}
                </button>
              </TableHead>
              <TableHead className="w-40 whitespace-nowrap">
                <button
                  type="button"
                  className="inline-flex items-center font-medium hover:text-foreground transition-colors"
                  onClick={() => handleSort("status")}
                >
                  Status {sortIcon("status")}
                </button>
              </TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                  Loading projects…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && paginated.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                  No projects yet.
                </TableCell>
              </TableRow>
            )}
            {paginated.map((project) => (
              <TableRow key={project.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-foreground">{project.name}</span>
                    {project.projectType && (
                      <Badge variant={projectTypeVariant[project.projectType] ?? "secondary"}>
                        {projectTypeLabel[project.projectType] ?? project.projectType}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                    <span className="font-medium">{project.code}</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Started {project.startDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {project.teamMembers} team members
                    </span>
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <StatusBadge status={project.status} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {canEditProject(project) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        asChild
                        title="Edit project"
                      >
                        <Link to={`/projects/${project.id}/edit`}>
                          <Pencil className="w-4 h-4" />
                        </Link>
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      asChild
                      title="View project"
                      className="text-brand-teal hover:white"
                    >
                      <Link to={`/projects/${project.id}`}>
                        <Eye className="w-4 h-4" />
                      </Link>
                    </Button>
                    {canEditProject(project) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Delete project"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteClick({ id: project.id, name: project.name })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <DeleteConfirmationDialog
        open={!!deletingProject}
        title="Delete Project"
        description="This will permanently delete the project and all its associated data including documents, templates, extractions, and reports. This action cannot be undone."
        itemName={deletingProject?.name}
        isBlocked={deleteBlocked}
        blockMessage={deleteBlockMessage}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingProject(null)}
      />

      {totalPages > 1 && (
        <div className="mt-4">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-disabled={currentPage === 1}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              {getPageNumbers().map((p, i) =>
                p === "ellipsis" ? (
                  <PaginationItem key={`ellipsis-${i}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={p === currentPage}
                      onClick={() => setPage(p)}
                      className="cursor-pointer"
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                )
              )}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-disabled={currentPage === totalPages}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </PageLayout>
  );
};

export default AllProjects;
